import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useState } from 'react';

interface PasswordRule {
	id: string;
	label: string;
	test: (value: string) => boolean;
}

const RULES: Array<PasswordRule> = [
	{
		id: 'length',
		label: 'At least 8 characters',
		test: (v) => v.length >= 8,
	},
	{
		id: 'upper',
		label: 'One uppercase letter',
		test: (v) => /[A-Z]/.test(v),
	},
	{
		id: 'lower',
		label: 'One lowercase letter',
		test: (v) => /[a-z]/.test(v),
	},
	{ id: 'digit', label: 'One number', test: (v) => /\d/.test(v) },
	{
		id: 'special',
		label: 'One special character',
		test: (v) => /[^A-Za-z0-9]/.test(v),
	},
];

interface PasswordSetFormProps {
	/**
	 * Heading shown above the form. Differs per use case (set initial
	 * password vs reset vs change).
	 */
	title: string;
	/**
	 * When provided, surfaces a "current password" input above the new
	 * password fields. Only used by the change-password flow.
	 */
	requireCurrentPassword?: boolean;
	/**
	 * Optional intro paragraph (e.g., "You're setting the password for
	 * a***@example.com").
	 */
	subtitle?: string;
	/**
	 * Submit handler. Called with the validated values once both new
	 * passwords match and pass the strength rules.
	 */
	onSubmit: (values: {
		currentPassword: string;
		newPassword: string;
	}) => Promise<void>;
	submitLabel: string;
	/**
	 * Server-side error message rendered above the submit button.
	 * Cleared whenever the user edits any field.
	 */
	errorMessage?: string | null;
	/** Loading flag from the parent (e.g. while the API call is in flight). */
	isSubmitting?: boolean;
}

/**
 * Shared password input form used by set-password (welcome), reset-
 * password, and change-password flows. Owns local strength validation
 * + confirm matching; the parent owns the API call and the
 * post-submission redirect/UX.
 */
export function PasswordSetForm({
	title,
	subtitle,
	requireCurrentPassword,
	onSubmit,
	submitLabel,
	errorMessage,
	isSubmitting,
}: PasswordSetFormProps): JSX.Element {
	const { t } = useTranslation();
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [submitted, setSubmitted] = useState(false);

	const ruleResults = RULES.map((r) => ({ ...r, passes: r.test(newPassword) }));
	const allRulesPass = ruleResults.every((r) => r.passes);
	const passwordsMatch =
		newPassword.length > 0 && newPassword === confirmPassword;
	const canSubmit =
		allRulesPass &&
		passwordsMatch &&
		(!requireCurrentPassword || currentPassword.length > 0) &&
		isSubmitting !== true;

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>,
	): Promise<void> => {
		e.preventDefault();
		setSubmitted(true);
		if (!canSubmit) return;
		await onSubmit({ currentPassword, newPassword });
	};

	return (
		<form onSubmit={handleSubmit} className="w-full max-w-md mx-auto space-y-4">
			<div>
				<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
					{t(title)}
				</h1>
				{subtitle != null && subtitle.length > 0 && (
					<p className="text-sm text-gray-600 dark:text-gray-400">
						{t(subtitle)}
					</p>
				)}
			</div>

			{requireCurrentPassword === true && (
				<label className="block">
					<span className="text-sm font-medium text-gray-700 dark:text-gray-200">
						{t('Current password')}
					</span>
					<input
						type="password"
						autoComplete="current-password"
						value={currentPassword}
						onChange={(e) => setCurrentPassword(e.target.value)}
						className="mt-1 block w-full rounded-md border border-brand-border bg-brand-input px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-portal-accent"
						required
					/>
				</label>
			)}

			<label className="block">
				<span className="text-sm font-medium text-gray-700 dark:text-gray-200">
					{t('New password')}
				</span>
				<input
					type="password"
					autoComplete="new-password"
					value={newPassword}
					onChange={(e) => setNewPassword(e.target.value)}
					className="mt-1 block w-full rounded-md border border-brand-border bg-brand-input px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-portal-accent"
					required
				/>
			</label>

			<label className="block">
				<span className="text-sm font-medium text-gray-700 dark:text-gray-200">
					{t('Confirm new password')}
				</span>
				<input
					type="password"
					autoComplete="new-password"
					value={confirmPassword}
					onChange={(e) => setConfirmPassword(e.target.value)}
					className="mt-1 block w-full rounded-md border border-brand-border bg-brand-input px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-portal-accent"
					required
				/>
				{submitted && newPassword.length > 0 && !passwordsMatch && (
					<span className="block mt-1 text-xs text-red-600 dark:text-red-400">
						{t('Passwords do not match')}
					</span>
				)}
			</label>

			<ul className="text-xs space-y-1">
				{ruleResults.map((r) => (
					<li
						key={r.id}
						className={
							r.passes
								? 'text-green-600 dark:text-green-400'
								: 'text-brand-muted'
						}
					>
						{r.passes ? '✓' : '○'} {t(r.label)}
					</li>
				))}
			</ul>

			{errorMessage != null && errorMessage.length > 0 && (
				<div className="rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
					{t(errorMessage)}
				</div>
			)}

			<button
				type="submit"
				disabled={!canSubmit}
				className="w-full rounded-md bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium py-2 transition-colors"
			>
				{isSubmitting === true ? t('Saving...') : t(submitLabel)}
			</button>
		</form>
	);
}
